import { BooleanOperations, Box, ORIENTATION, Polygon } from '@flatten-js/core'
import {
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoMarkupPolygon,
  GeckoMarkupPrimitive,
  GeckoMarkupRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

const gap = 0.0001

export function boxArea(
  primitive: GeckoMarkupRect,
  componentBounds: GeckoComponentRect
): number {
  const poly = new Polygon()
  const boundsInPrimitiveUnits = componentBounds.project(primitive.bounds.space)
  const primitiveBounds = getMinMaxs(primitive)

  if (!primitiveBounds) {
    return 0
  }

  if (primitiveBounds['x_start'] <= primitiveBounds['x_end']) {
    poly.addFace(
      new Box(
        primitiveBounds['x_start'],
        primitiveBounds['y_start'],
        primitiveBounds['x_end'],
        primitiveBounds['y_end']
      )
    )
  } else {
    poly.addFace(
      new Box(
        boundsInPrimitiveUnits.x.start.num,
        primitiveBounds['y_start'],
        primitiveBounds['x_end'],
        primitiveBounds['y_end']
      )
    )
    poly.addFace(
      new Box(
        primitiveBounds['x_start'],
        primitiveBounds['y_start'],
        boundsInPrimitiveUnits.x.end.num,
        primitiveBounds['y_end']
      )
    )
  }
  return poly.area()
}

export function getPrettyDimensions(
  rect: GeckoMarkupRect,
  geometry: GeckoComponentRect
): string {
  const bounds = getMinMaxs(rect)
  if (bounds.x_start < bounds.x_end) {
    return rect.rect.x.size.toString() + ' x ' + rect.rect.y.size.toString()
  } else {
    const geoMin = geometry.start.x.num
    const geomMax = geometry.end.x.num
    const realDelta = bounds.x_end - geoMin + (geomMax - bounds.x_start)
    const realMeasurement = GeckoMeasurement.CreateFromValue(
      realDelta,
      rect.rect.x.unit
    )
    return realMeasurement.toString() + ' x ' + rect.rect.y.size.toString()
  }
}

export function areaCalc(
  primitives: ReadonlyArray<GeckoMarkupPrimitive>,
  componentModel: GeckoComponentModel
): number {
  const poly = new Polygon()
  primitives
    .filter((primitive) => primitive.kind === 'rect')
    .forEach((primitive) => {
      const bounds = getMinMaxs(primitive as GeckoMarkupRect)
      const geoMin = componentModel.componentBounds.start.x.num
      const geoMax = componentModel.componentBounds.end.x.num
      if (bounds.x_start <= bounds.x_end) {
        const face = poly.addFace(
          new Box(
            //Fix float quirks
            Number((bounds.x_start - gap).toPrecision(12)),
            Number((bounds.y_start - gap).toPrecision(12)),
            Number((bounds.x_end + gap).toPrecision(12)),
            Number((bounds.y_end + gap).toPrecision(12))
          )
        )
        if (face.orientation() !== ORIENTATION.CCW) {
          // Known bug with flatten where merging Polygons with different orientations
          // can cause errors, just in case enforce same orientation.
          face.reverse()
        }
      } else {
        const face1 = poly.addFace(
          new Box(
            //Fix float quirks
            Number((geoMin - gap).toPrecision(12)),
            Number((bounds.y_start - gap).toPrecision(12)),
            Number((bounds.x_end + gap).toPrecision(12)),
            Number((bounds.y_end + gap).toPrecision(12))
          )
        )
        if (face1.orientation() !== ORIENTATION.CCW) {
          // Known bug with flatten where merging Polygons with different orientations
          // can cause errors, just in case enforce same orientation.
          face1.reverse()
        }
        const face2 = poly.addFace(
          new Box(
            //Fix float quirks
            Number((bounds.x_start - gap).toPrecision(12)),
            Number((bounds.y_start - gap).toPrecision(12)),
            Number((geoMax + gap).toPrecision(12)),
            Number((bounds.y_end + gap).toPrecision(12))
          )
        )
        if (face2.orientation() !== ORIENTATION.CCW) {
          // Known bug with flatten where merging Polygons with different orientations
          // can cause errors, just in case enforce same orientation.
          face2.reverse()
        }
      }
    })

  const islands = poly.splitToIslands()
  try {
    const reducedPoly = polyReducer(islands)
    if (!reducedPoly) {
      return 0
    }

    if (componentModel.type === GeckoComponentModelType.LegacyBoilerWalls) {
      const tubeScaledPoly = reducedPoly.scale(
        componentModel.tubeSpacing?.convertTo(GeckoMeasurementUnit.Feet).num ??
          1,
        1
      )
      return tubeScaledPoly.area()
    }

    return reducedPoly.area()
  } catch (error) {
    console.error(error)
    return 0
  }
}

type Bounds = {
  x_start: number
  x_end: number
  y_start: number
  y_end: number
}

export function getMinMaxs(
  primitive: GeckoMarkupPrimitive | undefined
): Bounds {
  if (!primitive) {
    return {
      x_start: 0,
      x_end: 0,
      y_start: 0,
      y_end: 0,
    }
  }

  if (primitive instanceof GeckoMarkupPolygon) {
    const xCoords = primitive.path.points.map((p) => p.x.num)
    const yCoords = primitive.path.points.map((p) => p.y.num)
    return {
      x_start: Math.min(...xCoords),
      x_end: Math.max(...xCoords),
      y_start: Math.min(...yCoords),
      y_end: Math.max(...yCoords),
    }
  }

  if (primitive instanceof GeckoMarkupRect) {
    return {
      x_start: primitive.rect.start.x.num,
      x_end: primitive.rect.end.x.num,
      y_start: primitive.rect.start.y.num,
      y_end: primitive.rect.end.y.num,
    }
  }

  return {
    x_start: 0,
    x_end: 0,
    y_start: 0,
    y_end: 0,
  }
}

export function within(
  coords: Record<string, number>,
  x: number,
  y: number
): boolean {
  if (coords['x_start'] <= coords['x_end']) {
    return (
      coords['x_start'] < x &&
      coords['x_end'] > x &&
      coords['y_end'] > y &&
      coords['y_start'] < y
    )
  } else {
    const withinY = coords['y_end'] > y && coords['y_start'] < y
    const withinX = x > coords['x_start'] || x < coords['x_end']
    return withinX && withinY
  }
}

function polyReducer(polygons: Polygon[]): Polygon | null {
  if (!polygons.length) {
    return null
  }

  const mergedPolys: Polygon[] = []
  for (let i: number = 0; i < polygons.length; i += 2) {
    const p0 = polygons.at(i)
    const p1 = polygons.at(i + 1)
    if (p0) {
      if (p1) {
        if (p0.isValid() && p1.isValid()) {
          mergedPolys.push(BooleanOperations.unify(p0, p1))
        } else {
          throw 'Invalid polygon encountered!'
        }
      } else {
        mergedPolys.push(p0)
      }
    }
  }
  if (mergedPolys.length === 1) {
    return mergedPolys[0]
  } else {
    return polyReducer(mergedPolys)
  }
}
